import { addDays } from "date-fns";
import Enumerable from "linq";
import { IDateRange } from "../../common/models/IDateRange";
import i18n from "../../services/localization-service/i18n";
import { GraphItemOptions, GraphOptions, IGraphSeriesOptions, ITrendDataResultsModel } from "../../services/trending-data-service/Models";
import { DataTooltipDisplayEnum } from "../../services/user-settings-data/Models";
import { CustomDurationDialogValues  } from "../custom-duration-dialog/CustomDurationDialog";
import GraphUtils from "../../common/utilities/GraphUtils";
import { IDrcxEffectiveTimeModel } from "../../common/models/drcx/DRCXEffectiveTimeModel";
import { GraphConfigurationData, GraphConfigurationDescription } from "../../services/trending-data-service/TrendConfigurationModels";
import { AccessPermission } from "../../common/models/AccessPermission";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { AccessType,  DateRangeType, DurationTimeframeTypeEnum } from "../../common/enums/DRCXEnums";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { isMobile } from "react-device-detect";
import { cloneEmptyHighchartsSeries } from "../../services/trending-data-service/UIChartProperties";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { SeriesLineOptions, SeriesOptionsType } from "highcharts";
import { IDRCXTimeAndDuration } from "../../common/models/drcx/DRCXTimeAndDuration";
import { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import { ObjectGraphOptionsDialogValues } from "./options-dialog/ObjectGraphOptionsUtils";
import _ from "lodash";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";


const ObjectGraphUtils = {

    getConfiguration(
        configurationData: GraphConfigurationData | undefined,
        configurationID: number | undefined
    ): GraphConfigurationDescription | undefined {
        return configurationData?.configurations?.find(o => o.id === configurationID);
    },
    getWritePermission(): AccessPermission {
        return new AccessPermission({
            functionalityType: AccessFunctionalityTypeEnum.Trending,
            access: AccessTypeEnum.Write_Change
        });
    },
    userHasPermission(
        configurationData: GraphConfigurationData | undefined,
        permission: AccessPermission
    ): boolean {
        return (configurationData?.userPermissions)?.find(o => o.contains(permission)) !== undefined;
    },
    getActions(
        hasGroupObjects: boolean,
        sourceActions: ActionMenuItem[],
        isPhone: boolean,
        currentConfigID: number | undefined,
        defaultConfigID: number | undefined,
        configurationData: GraphConfigurationData | undefined,
        hasData: boolean,
        offline: boolean,
        disabledActions: Array<BaseActionEnum>,
        hiddenActions: Array<BaseActionEnum>,
        quickGraph: boolean
        //canRunOrDownload: boolean
    ): ActionMenuItem[] {

        const actions = _.cloneDeep(sourceActions);

        const hasConfigurations = (configurationData?.configurations?.length ?? 0) > 0;
        const configuration = ObjectGraphUtils.getConfiguration(configurationData, currentConfigID);

        const userHasWriteAccessToConfiguration = (configuration?.access === AccessType.Private) ||
            configuration?.userHasPermission(ObjectGraphUtils.getWritePermission());

        const userHasModifyPermission = ObjectGraphUtils.userHasPermission(configurationData, ObjectGraphUtils.getWritePermission());

        let canEditConfiguration = false;
        let canDuplicateConfiguration = false;
        let canSetViewAsDefault = false;
        let canModifyConfigurationPermissions = false;
        let canDeleteConfiguration = false;
        let canRenameConfiguration = false;
        const canModify = hasConfigurations;

        switch (configuration?.access) {

            case AccessType.Private:
                canEditConfiguration = canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID;
                canDeleteConfiguration = canModify;
                canRenameConfiguration = canModify;
                break;

            case AccessType.Shared:
                canEditConfiguration = userHasModifyPermission && canModify;
                canDuplicateConfiguration = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID || userHasModifyPermission;
                canModifyConfigurationPermissions = userHasModifyPermission && canModify;
                canDeleteConfiguration = userHasModifyPermission && canModify;
                canRenameConfiguration = userHasModifyPermission && canModify;
                break;

            case AccessType.SharedReadOnly:
                canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDuplicateConfiguration = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID || userHasModifyPermission;
                canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                break;

            default:
                break;
        }

        const configureAction: ActionMenuItem | undefined = actions.find(o => o.action === BaseActionEnum.Configure);

        if (currentConfigID !== undefined && configureAction) {
            if (currentConfigID === -1) {

                configureAction.subActions = _.cloneDeep((configureAction.subActions ?? [])).filter(o => o.action !== BaseActionEnum.Edit);
            } 
        }        

        const configurationSelected = (configuration?.id ?? -1) > -1;

        // first determine which actions are visible
        const visibleActions = hasGroupObjects
            ? (((hiddenActions?.length ?? 0) > 0)
                ? actions.filter(o => !hiddenActions.includes(o.action as BaseActionEnum))
                
                : actions)
                .filter(action => {


                    switch (action.action) {

                        case BaseActionEnum.Export:
                            if (offline) {
                                return false;
                            }

                            action.subActions = (action.subActions ?? []).filter(v => {
                                if (v.action === BaseActionEnum.SaveGraphImage) {
                                    return !isMobile;
                                } else if (v.action === BaseActionEnum.SaveQuickGraph) {
                                    return quickGraph;
                                } else {
                                    return true;
                                }
                            });

                            return true;

                        case BaseActionEnum.Configure:

                            action.subActions = (action.subActions ?? []).filter(v => {
                                if (v.action === BaseActionEnum.Edit) {
                                    return !isPhone && configurationSelected && canEditConfiguration;
                                }
                                else if (v.action === BaseActionEnum.CreateNew) {
                                    return !isPhone;

                                } else {
                                   return !isPhone && configurationSelected
                                }
                            });

                            return true;

                        default:
                            return true;
                    }
                }) : [];

        // next determine the state of the visible actions
        let disabledStateActions = visibleActions
            .map(action => {
                const actionCopy = { ...action };

                switch (action.action) {

                    case BaseActionEnum.Export:
                        actionCopy.disabled = !hasData; // !canRunOrDownload;

                        (actionCopy.subActions ?? []).forEach(v => {
                            v.disabled = !hasData;
                        });

                        break;

                    case BaseActionEnum.Configure:
                        actionCopy.subActions = action.subActions?.map(subAction => {
                            const copy = { ...subAction };

                            switch (subAction.action) {

                                case BaseActionEnum.Edit:
                                    copy.disabled = !canEditConfiguration;
                                    break;

                                case BaseActionEnum.CreateNew:
                                    // should always be enabled
                                    copy.disabled = false;
                                    break;

                                case BaseActionEnum.Duplicate:
                                    copy.disabled = !canDuplicateConfiguration;
                                    break;

                                case BaseActionEnum.ModifyPermissions:
                                    copy.disabled = !canModifyConfigurationPermissions;
                                    break;

                                case BaseActionEnum.Delete:
                                    copy.disabled = !canDeleteConfiguration;
                                    break;

                                case BaseActionEnum.Rename:
                                    copy.disabled = !canRenameConfiguration;
                                    break;

                                case BaseActionEnum.SetAsDefault:
                                    copy.disabled = !canSetViewAsDefault;
                                    break;

                                default:
                                    copy.disabled = !canModify;
                            }

                            return copy;
                        });

                        break;

                }

                return actionCopy;
            });

        if ((disabledActions?.length ?? 0) > 0) {
            disabledStateActions.forEach(o => {
                if (disabledActions.includes(o.action as BaseActionEnum)) {
                    o.disabled = true;
                }
            });
        }


        if (quickGraph) {
            disabledStateActions = disabledStateActions.filter(o => o.action !== BaseActionEnum.Configure);
        } else {
            disabledStateActions = disabledStateActions.filter(o => o.action !== BaseActionEnum.SaveQuickGraph);
        }

        return disabledStateActions;
    },
    getActionsV2(
        hasGroupObjects: boolean,
        sourceActions: ActionMenuItem[],
        isPhone: boolean,
        currentConfigID: number | undefined,
        defaultConfigID: number | undefined,
        configurationData: GraphConfigurationData | undefined,
        hasData: boolean,
        offline: boolean,
        disabledActions: Array<BaseActionEnum>,
        hiddenActions: Array<BaseActionEnum>,
        quickGraph: boolean
        //canRunOrDownload: boolean
    ): ActionMenuItem[] {

        const actions = _.cloneDeep(sourceActions);

        const hasConfigurations = (configurationData?.configurations?.length ?? 0) > 0;
        const configuration = ObjectGraphUtils.getConfiguration(configurationData, currentConfigID);

        const userHasWriteAccessToConfiguration = (configuration?.access === AccessType.Private) ||
            configuration?.userHasPermission(ObjectGraphUtils.getWritePermission());

        const userHasModifyPermission = ObjectGraphUtils.userHasPermission(configurationData, ObjectGraphUtils.getWritePermission());

        let canEditConfiguration = false;
        let canDuplicateConfiguration = false;
        let canSetViewAsDefault = false;
        let canModifyConfigurationPermissions = false;
        let canDeleteConfiguration = false;
        let canRenameConfiguration = false;
        const canModify = hasConfigurations;

        switch (configuration?.access) {

            case AccessType.Private:
                canEditConfiguration = canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID;
                canDeleteConfiguration = canModify;
                canRenameConfiguration = canModify;
                break;

            case AccessType.Shared:
                canEditConfiguration = userHasModifyPermission && canModify;
                canDuplicateConfiguration = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID || userHasModifyPermission;
                canModifyConfigurationPermissions = userHasModifyPermission && canModify;
                canDeleteConfiguration = userHasModifyPermission && canModify;
                canRenameConfiguration = userHasModifyPermission && canModify;
                break;

            case AccessType.SharedReadOnly:
                canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDuplicateConfiguration = canModify;
                canSetViewAsDefault = currentConfigID !== defaultConfigID || userHasModifyPermission;
                canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                break;

            default:
                break;
        }

        const configureAction: ActionMenuItem | undefined = actions.find(o => o.action === BaseActionEnum.Configure);

        if (currentConfigID !== undefined && configureAction) {
            if (currentConfigID === -1) {

                //configureAction.subActions = _.cloneDeep((configureAction.subActions ?? [])).filter(o => o.action !== BaseActionEnum.Edit);
                const editAction = configureAction.subActions?.find(o => o.action === BaseActionEnum.Edit);
                if (editAction) {
                    editAction.disabled = true;
                }

            } 
        }        

        const configurationSelected = (configuration?.id ?? -1) > -1;

        // first determine which actions are visible
        // const visibleActions = hasGroupObjects
        //     ? (((hiddenActions?.length ?? 0) > 0)
        //         ? actions.filter(o => !hiddenActions.includes(o.action as BaseActionEnum))
                
        //         : actions)
        //         .filter(action => {


        //             switch (action.action) {

        //                 case BaseActionEnum.Export:
        //                     if (offline) {
        //                         return false;
        //                     }

        //                     action.subActions = (action.subActions ?? []).filter(v => {
        //                         if (v.action === BaseActionEnum.SaveGraphImage) {
        //                             return !isMobile;
        //                         } else if (v.action === BaseActionEnum.SaveQuickGraph) {
        //                             return quickGraph;
        //                         } else {
        //                             return true;
        //                         }
        //                     });

        //                     return true;

        //                 case BaseActionEnum.Configure:

        //                     action.subActions = (action.subActions ?? []).filter(v => {
        //                         if (v.action === BaseActionEnum.Edit) {
        //                             return !isPhone && configurationSelected && canEditConfiguration;
        //                         }
        //                         else if (v.action === BaseActionEnum.CreateNew) {
        //                             return !isPhone;

        //                         } else {
        //                            return !isPhone && configurationSelected
        //                         }
        //                     });

        //                     return true;

        //                 default:
        //                     return true;
        //             }
        //         }) : [];



        actions.forEach(action => {

                switch (action.action) {

                    case BaseActionEnum.Export:

                        action.subActions?.forEach(v => {
                            if (v.action === BaseActionEnum.SaveGraphImage) {
                                v.disabled = isMobile || offline;
                            } else if (v.action === BaseActionEnum.SaveQuickGraph) {
                                v.disabled = !quickGraph;
                            } else if (v.action === BaseActionEnum.Excel || v.action === BaseActionEnum.PDF) {
                                v.disabled = quickGraph;
                            } else {
                                v.disabled = false;
                            }
                        });

                        break;

                    case BaseActionEnum.Configure:

                        action.subActions?.forEach(o => {

                            switch (o.action) {
                                case BaseActionEnum.Edit:
                                    o.disabled = isPhone || !configurationSelected || !canEditConfiguration;
                                    break;
                            
                                case BaseActionEnum.CreateNew:
                                    o.disabled = isPhone;
                                    break;


                                case BaseActionEnum.Duplicate:
                                    o.disabled = !canDuplicateConfiguration;
                                    break;

                                case BaseActionEnum.ModifyPermissions:
                                    o.disabled = !canModifyConfigurationPermissions;
                                    break;

                                case BaseActionEnum.Delete:
                                    o.disabled = !canDeleteConfiguration;
                                    break;

                                case BaseActionEnum.Rename:
                                    o.disabled = !canRenameConfiguration;
                                    break;

                                case BaseActionEnum.SetAsDefault:
                                    o.disabled = !canSetViewAsDefault;
                                    break;


                                default:
                                    o.disabled = isPhone || configurationSelected || !canModify;
                                    break;
                            }
                        });
                        break;

                    default:
                        break;
                }
            })

        // // next determine the state of the visible actions
        // let disabledStateActions = visibleActions
        //     .map(action => {
        //         const actionCopy = { ...action };

        //         switch (action.action) {

        //             case BaseActionEnum.Export:
        //                 actionCopy.disabled = !hasData; // !canRunOrDownload;

        //                 (actionCopy.subActions ?? []).forEach(v => {
        //                     v.disabled = !hasData;
        //                 });

        //                 break;

        //             case BaseActionEnum.Configure:
        //                 actionCopy.subActions = action.subActions?.map(subAction => {
        //                     const copy = { ...subAction };

        //                     switch (subAction.action) {

        //                         case BaseActionEnum.Edit:
        //                             copy.disabled = !canEditConfiguration;
        //                             break;

        //                         case BaseActionEnum.CreateNew:
        //                             // should always be enabled
        //                             copy.disabled = false;
        //                             break;

        //                         case BaseActionEnum.Duplicate:
        //                             copy.disabled = !canDuplicateConfiguration;
        //                             break;

        //                         case BaseActionEnum.ModifyPermissions:
        //                             copy.disabled = !canModifyConfigurationPermissions;
        //                             break;

        //                         case BaseActionEnum.Delete:
        //                             copy.disabled = !canDeleteConfiguration;
        //                             break;

        //                         case BaseActionEnum.Rename:
        //                             copy.disabled = !canRenameConfiguration;
        //                             break;

        //                         case BaseActionEnum.SetAsDefault:
        //                             copy.disabled = !canSetViewAsDefault;
        //                             break;

        //                         default:
        //                             copy.disabled = !canModify;
        //                     }

        //                     return copy;
        //                 });

        //                 break;

        //         }

        //         return actionCopy;
        //     });

        // if ((disabledActions?.length ?? 0) > 0) {
        //     disabledStateActions.forEach(o => {
        //         if (disabledActions.includes(o.action as BaseActionEnum)) {
        //             o.disabled = true;
        //         }
        //     });
        // }


        if (quickGraph) {
            return actions.filter(o => o.action !== BaseActionEnum.Configure);
        } else {
            return actions.filter(o => o.action !== BaseActionEnum.SaveQuickGraph);
        }

        // if (quickGraph) {
        //     disabledStateActions = disabledStateActions.filter(o => o.action !== BaseActionEnum.Configure);
        // } else {
        //     disabledStateActions = disabledStateActions.filter(o => o.action !== BaseActionEnum.SaveQuickGraph);
        // }

        //return disabledStateActions;
    },
    getComparisonSectionNames(): Array<string> {
        return [
            i18n.t("trending.primaryGraph.title"),
            i18n.t("trending.comparisonGraph.title")
        ]
    },
    getConfiguredDuration(effectiveTime: IDrcxEffectiveTimeModel | undefined): CustomDurationDialogValues {

        if (effectiveTime?.startTime && effectiveTime?.endTime) {

            return {
                option: DurationOptionEnum.Default,
                duration: {
                    startDate: new Date(effectiveTime.startTime),
                    endDate: new Date(effectiveTime.endTime)
                }
            };
        } else {
            return {
                option: DurationOptionEnum.Default,
                duration: {
                    startDate: addDays(new Date(), -7),
                    endDate: new Date()
                }
            };
        }
    },
    getCustomDurationLabel(option: DurationOptionEnum): string {

        switch (option) {

            case DurationOptionEnum.OneDay:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.1Day')}`;

            case DurationOptionEnum.ThreeDays:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.3Days')}`;

            case DurationOptionEnum.SevenDays:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.7Days')}`;

            case DurationOptionEnum.ThirtyDays:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.30Days')}`;

            case DurationOptionEnum.Custom:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.custom1')}`;

            default:
                return `${i18n.t('customDuration.duration')}: ${i18n.t('customDuration.configured')}`;
        }
    },
    getDefaultDisplayOptions(): ObjectGraphOptionsDialogValues {

        return {
            groupID: -1,
            contextObject: {
                id: -1,
                objectType: eLynxObjectTypeEnum.None,
            },
            contextObjectType: eLynxObjectTypeEnum.None,
            displayOptions: {
            comparisonTimeAndDuration: undefined,
            displayAlarms: false,
            displayDataQualityEvents: false,
            displayDiagnosticEvents: false,
            displayManualEvents: false,
            displayLegend: true,
            displayMemos: false,
            displaySectionsStacked: false,
            displayTabularData: false,
            displaySettingsChanges: false,
            displaySimulationDataQualityEvents: false,
            displaySimulationDiagnosticEvents: false,
            hasComparisonTimeframe: false
            },
            durationOptions: {
                option: DurationOptionEnum.Default,
                duration: {
                    startDate: addDays(new Date(), -7),
                    endDate: new Date()
                }
            }
        }
    },
    getGraphNoDataMessage(dataConfigID: number | undefined, 
        objectName: string, 
        dateRange: IDateRange | undefined, 
        isUnsaved: boolean,
        comparisonDuration: IDRCXTimeAndDuration | undefined): string {

        const configID: number = dataConfigID ?? -1;

        let message = '';

        if (isUnsaved || configID === -1) {

            message = i18n.t("trending.messages.selectConfig");

        } else {

            if (comparisonDuration) {

                switch(comparisonDuration.durationType) {
                    case DurationTimeframeTypeEnum.MultipleTimeFrames:
                        message = i18n.t("noData.noDataComparisonBeginning", {
                            offset: comparisonDuration.multipleStartOffset,
                            type: `${DateRangeType[comparisonDuration.multipleStartRangeType as DateRangeType].toLowerCase()}(s)`
                        });
                        break;

                        
                    // case DurationTimeframeTypeEnum.CurrentSingleTimeFrame:
                    //     break;
                    
                    // case DurationTimeframeTypeEnum.PreviousSingleTimeFrame:
                    //     break;

                    case DurationTimeframeTypeEnum.SpecificStartOrStop:
                        message = i18n.t("noData.noDataComparisonSpecificStart", {
                            date: comparisonDuration.fixedStartDate?.toLocaleString()
                            
                        });
                        break;                        
                }
            } else if (dateRange && dateRange.startDate && dateRange.endDate) {
                message = i18n.t("noData.noDataForObjectWithinDateRange", {
                    startDate: dateRange.startDate.toLocaleString(),
                    endDate: dateRange.endDate.toLocaleString(),
                    objectName: objectName
                });

            } else {
                message = i18n.t("noData.noDataForObject", { objectName: objectName });
            }
        }

        return message;
    },
    getGraphOptions(contextObject: IeLynxObject | undefined, trendData: ITrendDataResultsModel | undefined,
        tooltipDisplayType: DataTooltipDisplayEnum,
        minYaxis: number | undefined,
        maxYaxis: number | undefined,
        forceYaxis?: number): GraphOptions | undefined {

        let graphOptions: GraphOptions | undefined = undefined;

        const isComparison = trendData?.displayOptions?.hasComparisonTimeframe ?? false;
        const isStacked = trendData?.displayOptions?.displaySectionsStacked ?? false;
        const synchronizeCharts = isComparison || isStacked;
        const synchronizeLegends = isComparison || isStacked;

        if (trendData?.trendViewModels) {

            // If they are stacked, then get the minimum and maximum chart times.
            let minChartTime: number | undefined = undefined;
            let maxChartTime: number | undefined = undefined;


            if (isStacked) {

                // If they are stacked, then get the minimum and maximum chart times.
                minChartTime = Enumerable.from(trendData.trendViewModels)
                    .min(tvm => tvm.effectiveTime?.minChartTime ?? 0);

                // maxChartTime = Enumerable.from(trendData.trendViewModels)
                //     .max(tvm => tvm?.effectiveTime?.maxChartTime ?? 0);

                // look for the maximum timestamp within the data to eliminate
                // whitespace at the end of the charts
                const items = Enumerable.from(trendData.trendViewModels)
                                .selectMany(tvm => tvm.chartOptions?.series ?? new Array<SeriesOptionsType>())
                                .selectMany(s => (s as SeriesLineOptions).data ?? [])
                                .toArray();

                if (items.length > 0) {
                    maxChartTime = Enumerable
                    .from(items)
                    .select(d => (d as any)[0])
                    .max();
                }

            } else if (isComparison) {

                // For Comparison Charts, the configured data is always the first one.
                const configuredData = trendData.trendViewModels[0];
                // For Comparison Charts, the historical data is always the second one.
                const historicalData = trendData.trendViewModels[1];

                if (configuredData?.chartOptions && historicalData?.chartOptions) {

                    const configXaxis: Highcharts.XAxisOptions[] = configuredData.chartOptions.xAxis as Highcharts.XAxisOptions[];
                    const histXaxis: Highcharts.XAxisOptions[] = historicalData.chartOptions.xAxis as Highcharts.XAxisOptions[];
                    const histYaxes: Highcharts.YAxisOptions[] = historicalData.chartOptions.yAxis as Highcharts.YAxisOptions[];

                    configXaxis[0].min = configuredData?.effectiveTime?.minChartTime;
                    configXaxis[0].max = configuredData?.effectiveTime?.maxChartTime;

                    histXaxis[0].min = historicalData?.effectiveTime?.minChartTime;
                    histXaxis[0].max = historicalData?.effectiveTime?.maxChartTime;

                    if (synchronizeLegends === true) {

                        // If the Legends are synchronized then hide the Legend for the
                        //  Configured Data chart (top chart) since we will only have one Legend
                        //  at the bottom for both charts.
                        configuredData.chartOptions.legend = {
                            ...configuredData.chartOptions.legend, ...{
                                enabled: false
                            }
                        }

                        // We must have yAxes in order to add the missing Series or Highcharts throws an error.
                        if ((histYaxes?.length ?? 0) > 0) {

                            // If the Legends are synchronized and there are series in the
                            //  Configured Data chart (top chart) that are not in the Historical chart (bottom chart),
                            //  then we must add a dummy series for each of them to Historical chart so it will appear
                            //  in the one Legend at the bottom. This is necessary to control the display
                            //  of the Series in the Configured Data chart (top chart).
                            const missingInConfig = Enumerable.from(configuredData?.chartOptions?.series ?? [])
                                .where(series1 => Enumerable.from(historicalData?.chartOptions?.series ?? [])
                                    .all(series2 => series2.name !== series1.name))
                                .toArray();

                            missingInConfig.forEach(series => {
                                historicalData?.chartOptions?.series &&
                                    historicalData.chartOptions.series
                                        .push(cloneEmptyHighchartsSeries(series as IGraphSeriesOptions));
                            });
                        }
                    }
                }
            }

            graphOptions = {

                isComparison: trendData.displayOptions?.hasComparisonTimeframe ?? false,
                isStacked: trendData.displayOptions?.displaySectionsStacked ?? false,
                listTextTagMapping: trendData.listTextTagMapping,
                synchronizeCharts: synchronizeCharts,
                synchronizeLegends: synchronizeLegends,

                // We must set the IDs here instead of Graph.tsx. React and Highcharts didn't work well
                //  together when they were defined in Graph.tsx. The DOM elements with the IDs were not there
                //  when Highcharts tried to attach to them.
                optionItems: Enumerable.from(trendData.trendViewModels)
                    .select((tvm, chartIndex) => {

                        const optionItems: GraphItemOptions | undefined = tvm.chartOptions as GraphItemOptions;

                        if (optionItems) {

                            if (isStacked) {

                                // We only allow x axis zooming for stacked charts.
                                if (optionItems.chart) {

                                    if (optionItems.chart.style) {
                                        optionItems.chart.style.lineWidth = 4;
                                    }

                                    optionItems.chart.zooming = {
                                        type: "x"
                                    }
                                }

                                const xAxis: Highcharts.XAxisOptions[] = optionItems.xAxis as Highcharts.XAxisOptions[];

                                xAxis[0].min = minChartTime;
                                xAxis[0].max = maxChartTime;

                            } else if (isComparison) {

                                // We only allow x axis zooming for comparison charts.
                                if (optionItems.chart) {

                                    //optionItems.chart.zoomType = "x";
                                    optionItems.chart.zooming = {
                                        type: 'x'
                                    }
                                }
                            }

                            optionItems.headerTitle = ((trendData.displayOptions?.hasComparisonTimeframe ?? false) ||
                                (trendData.displayOptions?.displaySectionsStacked ?? false)) &&
                                ((trendData.sectionNames?.length ?? 0) > chartIndex)
                                ? (trendData.sectionNames ?? [])[chartIndex] : undefined;

                            optionItems.id = GraphUtils.getChartID();
                            optionItems.index = chartIndex;

                            // Override the data tooltip settings with the User's preference.
                            optionItems.tooltip = {
                                ...optionItems.tooltip, ...{
                                    shared: (tooltipDisplayType ?? DataTooltipDisplayEnum.Shared) ===
                                        DataTooltipDisplayEnum.Shared
                                }
                            }

                            optionItems.exporting = GraphUtils.getGraphExportingOptions(contextObject?.objectName, optionItems);
                        }

                        
                        // User Story 52997: Graph Options - Add Line Weight, at some point it was decided to always use a width of 2 pixels, but NO MORE
                        //optionItems.series?.filter(o => o.type === 'line').forEach(o => (o as SeriesLineOptions).lineWidth = 2);

                        if (forceYaxis !== undefined && optionItems.yAxis !== undefined) {
                            const yAxis = optionItems.yAxis as Highcharts.YAxisOptions[];
                            if (yAxis && yAxis.length > 0 && yAxis[0]) {
                                yAxis[0].min = forceYaxis;
                            }
                            //yAxis[0].min = forceYaxis;                            
                        } else if (minYaxis !== undefined && maxYaxis !== undefined) {


                            let minValue = minYaxis;
                            let minYSeriesValue = minYaxis;

                            // User Story 54101: Graph Tools - Override for Force Min/Max
                            // Highcharts Error #10 happens because you are trying to put a zero, a negative number, 
                            // or a null value onto a logarithmic axis                            
                            const items = Enumerable.from(tvm.chartOptions?.series ?? new Array<SeriesOptionsType>())
                                            .selectMany(s => (s as SeriesLineOptions).data ?? [])
                                            .toArray();

                            if (items.length > 0) {
                                minYSeriesValue = Enumerable
                                .from(items)
                                .select(d => (d as any)[1])
                                .min();
                            }

                            if (minYaxis < minYSeriesValue) {
                                minValue = minYSeriesValue - 0.0001;
                            }

                            const yAxis = optionItems.yAxis as Highcharts.YAxisOptions[];
                            // if (yAxis && yAxis.length > 0 && yAxis[0]) {
                            //     yAxis[0].min = minYaxis;
                            //     yAxis[0].max = maxYaxis;
                            // }
                            
                            if (yAxis) {

                                yAxis.forEach(o => {
                                   
                                    //o.min = minYaxis;
                                    //User Story 54101: Graph Tools - Override for Force Min/Max if type is logarithmic then 
                                    // the min cannot be zero or negative
                                    o.min = o.type === 'logarithmic' ? minValue : minYaxis;
                                    o.max = maxYaxis;
                                });
                            }
                        }

                        return optionItems;
                    })
                    ?.toArray() ?? []
            }
        }

        return graphOptions;
    }
};

export default ObjectGraphUtils;
